import { baseApi } from 'services'

import {
  AVMAddressAudit,
  AppraisalReportAddressAudit,
  CustomerDocument,
  DocumentReview,
  PaginatedResults,
  QuarterlyCustomerStatement,
  UserDocument,
} from '@/types/unlock'
import { downloadBlob } from '@/utils/documents'

export const documentsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    listUserDocument: builder.query<UserDocument[], string | number | void>({
      query: (applicantIds) => ({
        url: 'v2/user-documents/',
        params: applicantIds ? { application_profile__in: applicantIds } : null,
      }),
      providesTags: () => ['Documents'],
    }),
    listUserDocumentByAppExternalId: builder.query<UserDocument[], string | number | void>({
      query: (appExternalId) => ({
        url: 'v2/user-documents/',
        params: appExternalId
          ? { application_profile__application__external_id: appExternalId }
          : null,
      }),
      providesTags: () => ['Documents'],
    }),
    documentUploadStart: builder.mutation<
      UserDocument,
      {
        tag?: string
        file_name?: string
        application_profile: number
        snapshot?: string
        kind?: string
        customer_visible?: boolean
        source?: string
        type?: string
        category?: string
        document?: string
        document_template?: number
      }
    >({
      query: ({
        tag,
        file_name,
        application_profile,
        snapshot,
        kind,
        customer_visible = false,
        source = 'max-system',
        type = null,
        category = null,
        document = null,
        document_template = null,
      }) => ({
        url: `v2/user-documents/start-upload/`,
        method: 'POST',
        body: {
          tag,
          file_name,
          application_profile,
          snapshot,
          kind,
          customer_visible,
          source,
          type,
          category,
          document,
          document_template,
        },
      }),
      invalidatesTags: ['Documents', 'MaxActions'],
    }),
    multiDocumentUploadStart: builder.mutation<
      UserDocument[],
      {
        tag?: string
        file_name: string
        application_profile: number
        snapshot?: string
        kind?: string
        customer_visible?: boolean
      }[]
    >({
      query: (body) => ({
        url: `v2/user-documents/start-multi-upload/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Documents'],
    }),
    documentUploadFinish: builder.mutation<UserDocument, string>({
      query: (uuid) => {
        return {
          url: `v2/user-documents/${uuid}/finish-upload/`,
          method: 'PATCH',
        }
      },
      invalidatesTags: ['Documents'],
    }),
    listDocument: builder.query<UserDocument[], string>({
      query: (snapshot) => `v2/user-documents/?snapshot=${snapshot}`,
      providesTags: () => ['Documents'],
    }),
    renameByType: builder.mutation<UserDocument, Partial<UserDocument>>({
      query: (data) => {
        const uuid = data.uuid
        delete data.uuid

        return {
          url: `v2/user-documents/${uuid}/rename-by-type/`,
          method: 'PATCH',
          body: data,
        }
      },
      invalidatesTags: ['Documents'],
    }),
    updateUserDocument: builder.mutation<UserDocument, Partial<UserDocument>>({
      query: (data) => {
        const uuid = data.uuid
        delete data.uuid

        return {
          url: `v2/user-documents/${uuid}/`,
          method: 'PATCH',
          body: data,
        }
      },
      invalidatesTags: ['Documents', 'Alert'],
    }),
    deleteUserDocument: builder.mutation<UserDocument, string>({
      query: (uuid) => ({
        url: `v2/user-documents/${uuid}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Documents', 'Alert'],
    }),
    listCustomerDocument: builder.query<CustomerDocument[], string>({
      query: (applicantIds) => ({
        method: 'GET',
        url: 'v2/customer-documents/',
        params: { application_profile__in: applicantIds },
      }),
      providesTags: () => ['Documents'],
    }),
    listCustomerDocumentByAppExternalId: builder.query<CustomerDocument[], string>({
      query: (appExternalId) => ({
        method: 'GET',
        url: 'v2/customer-documents/',
        params: { application_profile__application__external_id: appExternalId },
      }),
      providesTags: () => ['Documents'],
    }),
    getDocumentReviews: builder.query<PaginatedResults<DocumentReview[]>, number>({
      query: (documentId) => ({
        url: 'v2/document-review/',
        params: { document__id: documentId },
      }),
      providesTags: ['DocumentReview'],
    }),
    createDocumentReview: builder.mutation<DocumentReview, Partial<DocumentReview>>({
      query: (body) => ({
        url: `v2/document-review/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['DocumentReview'],
    }),
    getDocumentMetadata: builder.query<any, string>({
      query: (documentUUID) => `v2/user-documents/${documentUUID}/metadata/`,
      providesTags: ['DocumentMetadata'],
    }),
    updateDocumentMetadata: builder.mutation<any, any>({
      query: (body) => ({
        url: `v2/document-metadata/${body.id}/`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['DocumentMetadata'],
    }),
    createDocumentAuditCheck: builder.mutation<any, string>({
      query: (documentMetadataId) => ({
        url: `v2/document-metadata/${documentMetadataId}/audit-check/`,
        method: 'POST',
      }),
      invalidatesTags: ['DocumentMetadataAuditCheck'],
    }),
    getDocumentAuditChecks: builder.query<any, string>({
      query: (documentMetadataId) => ({
        url: `v2/document-metadata-audit-check/`,
        params: { document_metadata__id: documentMetadataId },
      }),
      providesTags: ['DocumentMetadataAuditCheck'],
    }),
    getAVMAuditAddress: builder.query<AVMAddressAudit[], number>({
      query: (applicationId) => ({
        url: `v2/document-metadata-audit-check/avm-address-audit?application_id=${applicationId}`,
      }),
    }),
    getAppraisalReportAuditAddress: builder.query<AppraisalReportAddressAudit[], number>({
      query: (applicationId) => ({
        url: `v2/document-metadata-audit-check/appraisal-report-address-audit?application_id=${applicationId}`,
      }),
    }),
    extractDocumentData: builder.mutation<any, string>({
      query: (documentUUID) => ({
        url: `documents/extract-data/${documentUUID}`,
        method: 'POST',
      }),
    }),
    reprocessDocumentData: builder.mutation<any, string>({
      query: (documentUUID) => ({
        url: `documents/reprocess/${documentUUID}`,
        method: 'POST',
      }),
      invalidatesTags: ['DocumentMetadata'],
    }),
    auditCheckAutoComplete: builder.mutation<
      any,
      { auditCheckId: number; autoCompleteFields: string[] }
    >({
      query: ({ auditCheckId, autoCompleteFields }) => ({
        url: `v2/document-metadata-audit-check/${auditCheckId}/autocomplete/`,
        method: 'PATCH',
        body: autoCompleteFields,
      }),
      invalidatesTags: ['DocumentMetadataAuditCheck'],
    }),
    docDraw: builder.mutation<object, object>({
      query: (data) => ({
        url: `v2/docgen/`,
        method: 'POST',
        body: data,
      }),
    }),
    getDocumentTemplates: builder.query<string[], void>({
      query: () => ({
        url: `v2/document-template/`,
      }),
    }),
    downloadDocuments: builder.mutation({
      queryFn: async (
        { documents, customDownloadFileName = 'download.zip' },
        api,
        extraOptions,
        baseQuery
      ) => {
        try {
          const suggestedName =
            documents.length === 1 ? documents[0].original_filename : customDownloadFileName

          const result = await baseQuery({
            url: `v2/user-documents/download-docs/`,
            method: 'POST',
            body: documents,
            responseHandler: (response) => response.blob(),
          })

          if (result.data) {
            downloadBlob(result.data as Blob, suggestedName)
          }

          return { data: null }
        } catch (error) {
          return {
            error: { status: 'CUSTOM_ERROR', data: error.message, error: undefined },
          }
        }
      },
    }),
  }),
})

export const {
  useListUserDocumentQuery,
  useLazyListUserDocumentQuery,
  useLazyListUserDocumentByAppExternalIdQuery,
  useListUserDocumentByAppExternalIdQuery,
  useLazyListCustomerDocumentQuery,
  useLazyListCustomerDocumentByAppExternalIdQuery,
  useListCustomerDocumentByAppExternalIdQuery,
  useRenameByTypeMutation,
  useUpdateUserDocumentMutation,
  useDeleteUserDocumentMutation,
  useListCustomerDocumentQuery,
  useListDocumentQuery,
  useDocumentUploadStartMutation,
  useDocumentUploadFinishMutation,
  useGetDocumentReviewsQuery,
  useCreateDocumentReviewMutation,
  useGetDocumentMetadataQuery,
  useUpdateDocumentMetadataMutation,
  useCreateDocumentAuditCheckMutation,
  useGetAVMAuditAddressQuery,
  useGetAppraisalReportAuditAddressQuery,
  useGetDocumentAuditChecksQuery,
  useExtractDocumentDataMutation,
  useReprocessDocumentDataMutation,
  useAuditCheckAutoCompleteMutation,
  useDocDrawMutation,
  useMultiDocumentUploadStartMutation,
  useGetDocumentTemplatesQuery,
  useDownloadDocumentsMutation,
} = documentsApi
